import { TableColumn } from "react-data-table-component";
import { CallRecord } from "types/api";
import {
  secondsToMMSS,
  transformStringToTimeZoneAndFullTime,
} from "utils/date-utils";
import { colors } from "styles/variables";
import CheckIcon from "@material-ui/icons/Check";
import { difference } from "pages/Users/columns";
import { Tooltip } from "@material-ui/core";
import { Reflection } from "pages/UserDetails/styles";
import { Typography } from "@mui/material";

const parseErrorDetails = (errorDetails: string | null | undefined) => {
  if (!errorDetails) return [];

  return errorDetails.split("; ").map((error) => {
    // Extract just the error code (first part before the colon)
    const code = error.split(":")[0].trim();
    return {
      code: code,
      message: getErrorMessage(code),
    };
  });
};

const getErrorMessage = (code: string) => {
  const errorMessages: { [key: string]: string } = {
    "11205":
      "Call failed as twilio is unable to connect with Moneta backend services",
    "11200":
      "Call failed as twilio is unable to connect with Moneta backend services",
    "15003":
      "We experienced a brief connectivity issue, but the call may have been recovered.",
    "17002": "Record not found as call was made before 30 days",
    "20404": "Record not found",
    "32021": "Caller verification failed (possible spam)",
    "32014": "Call is terminated because of no audio received from patient",
    FAILED: "The call could not be connected.",
    BUSY: "The destination line was busy",
    "NO-ANSWER": "The call rang but was not answered.",
  };

  return errorMessages[code.toUpperCase()] || "Unknown error";
};

export const USAGE_HISTORY_TABLE_COLUMNS: TableColumn<CallRecord>[] = [
  {
    name: "Date",
    selector: (row) =>
      transformStringToTimeZoneAndFullTime(
        row.createdOn,
        row.account?.timeZone,
        false
      ),
    cell: (row) =>
      transformStringToTimeZoneAndFullTime(
        row.createdOn,
        row.account?.timeZone,
        true
      ),
    sortable: true,
  },
  {
    name: "Week",
    selector: (row) => {
      const title = row?.playlist?.title ?? "";
      return title.replace(/week\s*/i, ""); // Remove "Week " or "week " case-insensitively
    },
    sortable: false,
  },
  {
    name: "Duration",
    selector: (row) => secondsToMMSS(row.durationInSeconds ?? 0),
    sortable: false,
  },
  {
    name: "Status",
    selector: (row) => row.outcome ?? "",
    sortable: false,
  },

  {
    name: "Direction",
    selector: (row) => row.type ?? "",
    cell: (row) =>
      row.type ? (
        <Tooltip
          title={<Typography fontSize={12}>{row?.type}</Typography>}
          placement="top"
          arrow
        >
          <Reflection>{row?.type}</Reflection>
        </Tooltip>
      ) : (
        ""
      ),
    sortable: false,
  },
  {
    name: "HBA 1",
    selector: (row) => {
      let id = row?.activities?.[0]?.voiceMessage?.id;
      let count = 0;
      row?.activities?.map((ele: any) => {
        if (ele?.voiceMessage?.id === id && ele?.isCompleted === true) {
          count++;
        }
      });
      return count;
    },
    sortable: false,
  },
  {
    name: "HBA 2",
    selector: (row) => {
      if (
        row?.activities?.[1]?.voiceMessage?.id !==
        row?.activities?.[0]?.voiceMessage?.id
      ) {
        let id = row?.activities?.[1]?.voiceMessage?.id;
        let count = 0;
        row?.activities?.map((ele: any) => {
          if (ele?.voiceMessage?.id === id && ele?.isCompleted === true) {
            count++;
          }
        });
        return count;
      } else {
        return 0;
      }
    },
    sortable: false,
  },
  {
    name: "HBA 3",
    selector: (row) => {
      if (
        row?.activities?.[2]?.voiceMessage?.id !==
          row?.activities?.[1]?.voiceMessage?.id &&
        row?.activities?.[2]?.voiceMessage?.id !==
          row?.activities?.[0]?.voiceMessage?.id
      ) {
        let id = row?.activities?.[2]?.voiceMessage?.id;
        let count = 0;
        row?.activities?.map((ele: any) => {
          if (ele?.voiceMessage?.id === id && ele?.isCompleted === true) {
            count++;
          }
        });
        return count;
      } else {
        return 0;
      }
    },
    sortable: false,
  },

  {
    name: "CSAT",
    selector: (row) => row.sid ?? "",
    cell: (row) => (row.sid ? row?.programRating : ""),
    sortable: false,
  },
  {
    name: "Hangup",
    selector: (row) => row.hangupDetails ?? "",
    cell: (row) => {
      try {
        const parsed = JSON.parse(row.hangupDetails || "{}");
        return parsed?.hangupBy || "-";
      } catch {
        return "";
      }
    },
    sortable: false,
  },
  {
    name: "Errors",
    selector: (row) => row.errorDetails ?? "",
    cell: (row) => {
      if (!row.errorDetails) {
        return <span>-</span>;
      }

      const errors = parseErrorDetails(row.errorDetails);

      if (errors.length === 0) return "";

      if (errors.length === 1) {
        return <span>{errors[0].message}</span>;
      }

      return <span>{errors[0].message}</span>;
    },
    sortable: false,
    width: "200px",
  },

  {
    name: "Comments",
    selector: (row) =>
      !!row?.reflectionOnLearnings ? row?.reflectionOnLearnings : "",
    sortable: false,
    cell: (row) =>
      !!row?.reflectionOnLearnings ? (
        <Tooltip
          title={
            <Typography fontSize={12}>{row?.reflectionOnLearnings}</Typography>
          }
          placement="top"
          arrow
        >
          <Reflection>{row?.reflectionOnLearnings}</Reflection>
        </Tooltip>
      ) : (
        ""
      ),
  },
];

export const conditionalRowStyles = [
  {
    when: (row: CallRecord) =>
      row.playlist?.isActive === true && row?.type !== "OUTBOUND_CALL_BDL",
    style: {
      backgroundColor: colors.paleBlue,
    },
  },
];
